import React, { useEffect, useMemo, useState } from "react";
import swal from "sweetalert";
import { API_URL } from "../../constant/API_Settings";
import { getStoredPermissions } from "../../utils/permissions";
import {
  getReportDateRangeBounds,
  handleReportDateRangeInput,
  todayIso,
} from "../../utils/reportDateRange";
import { downloadReportPdf } from "../../utils/reportFormat";

/**
 * Financial Statements body — view on screen, then PDF / Excel download.
 * Used standalone (FinancialStatements page) and embedded (Owner Dashboard → Reports → Financials).
 */
export const moneyFormat = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(Number(value || 0));

const authHeaders = () => ({
  "Access-Control-Allow-Origin": "*",
  "Content-Type": "application/json",
  Authorization: "Bearer " + localStorage.getItem("AUTH_TOKEN") + "",
  Accept: "application/json",
});

const FinancialStatementsPanel = ({
  embedded = false,
  ownerVerified = false,
  syncDateRange = null,
  dateRangeKey = 0,
  hideDateFilter = false,
  sectionId = "owner-financial-statements",
}) => {
  const permissions = getStoredPermissions();

  const [inputValues, setInputValues] = useState({ start_date: "", end_date: "" });
  const [errors, setErrors] = useState({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");

  const today = todayIso();
  const dateBounds = useMemo(
    () => getReportDateRangeBounds({
      startDate: inputValues.start_date,
      endDate: inputValues.end_date,
      today,
    }),
    [inputValues.start_date, inputValues.end_date, today],
  );
  const hasAccess =
    embedded ||
    ownerVerified ||
    permissions.includes("All Reports") ||
    permissions.includes("Balance Sheet") ||
    permissions.includes("Simplified Balance Sheet") ||
    permissions.includes("Profit Loss");

  useEffect(() => {
    if (!embedded || !syncDateRange?.from_date || !syncDateRange?.to_date) {
      return;
    }
    setInputValues({
      start_date: syncDateRange.from_date,
      end_date: syncDateRange.to_date,
    });
  }, [embedded, syncDateRange?.from_date, syncDateRange?.to_date, dateRangeKey]);

  useEffect(() => {
    if (!embedded || !syncDateRange?.from_date || !syncDateRange?.to_date) {
      return;
    }
    loadReport(syncDateRange.from_date, syncDateRange.to_date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, dateRangeKey, syncDateRange?.from_date, syncDateRange?.to_date]);

  const handleInput = (e) => {
    if (e.target.name === "start_date" || e.target.name === "end_date") {
      handleReportDateRangeInput(e, inputValues, setInputValues, { today });
      return;
    }
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
  };

  function validateForm(start = inputValues.start_date, end = inputValues.end_date) {
    const errs = {};
    let ok = true;
    if (!start) {
      errs.start_date = "Choose start date";
      ok = false;
    }
    if (!end) {
      errs.end_date = "Choose end date";
      ok = false;
    }
    setErrors(errs);
    return ok;
  }

  function loadReport(startDate = inputValues.start_date, endDate = inputValues.end_date) {
    if (!validateForm(startDate, endDate)) return;
    setLoading(true);
    setReport(null);
    fetch(`${API_URL}/op-financial-statements`, {
      method: "post",
      headers: authHeaders(),
      body: JSON.stringify({ start_date: startDate, end_date: endDate }),
    })
      .then((response) => response.json())
      .then((data) => {
        setLoading(false);
        if (data.status === false) {
          swal("", data.message || "Unable to load report", "warning");
          return;
        }
        setReport(data);
      })
      .catch(() => {
        setLoading(false);
        swal("", "Unable to load report. Please try again.", "warning");
      });
  }

  async function downloadFile(kind) {
    if (!validateForm()) return;
    setDownloading(kind);
    const body = {
      start_date: inputValues.start_date,
      end_date: inputValues.end_date,
    };

    try {
      if (kind === "pdf") {
        await downloadReportPdf(
          `${API_URL}/op-financial-statements/pdf`,
          body,
          "FinancialStatements.pdf"
        );
        return;
      }

      const response = await fetch(`${API_URL}/op-financial-statements/excel`, {
        method: "post",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error("Unable to download the file.");
      }
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "FinancialStatements.xlsx";
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      swal("", error.message || "Unable to download the file. Please try again.", "warning");
    } finally {
      setDownloading("");
    }
  }

  const pl = report ? report.profit_loss : null;
  const bs = report ? report.balance_sheet : null;

  if (!hasAccess) {
    return (
      <div className="text-center text-muted py-3">
        You don&apos;t have permission to view financial statements.
      </div>
    );
  }

  return (
    <div id={sectionId}>
      {!hideDateFilter && (
        <div className="row mb-3">
          <div className="col-md d-flex flex-wrap align-items-end">
            <div className="text-start d-flex pe-2 mt-3">
              <div className="form-group">
                <label>Start date<sup className="star">*</sup></label>
                <input
                  type="date"
                  className="form-control"
                  name="start_date"
                  value={inputValues.start_date}
                  min={dateBounds.startMin || undefined}
                  max={dateBounds.startMax || undefined}
                  onChange={handleInput}
                />
                <div className="errorMsg">{errors.start_date}</div>
              </div>
            </div>
            <div className="text-start d-flex pe-2 mt-3">
              <div className="form-group">
                <label>End date<sup className="star">*</sup></label>
                <input
                  type="date"
                  className="form-control"
                  name="end_date"
                  value={inputValues.end_date}
                  min={dateBounds.endMin || undefined}
                  max={dateBounds.endMax || undefined}
                  onChange={handleInput}
                />
                <div className="errorMsg">{errors.end_date}</div>
              </div>
            </div>
            <div className="text-start d-flex mt-3">
              <button
                type="button"
                className="btn btn-primary mb-md-0 px-3 mb-3 ms-2"
                onClick={() => loadReport()}
                disabled={loading}
              >
                {loading ? "Loading..." : "View Report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {embedded && loading && !report && (
        <p className="small text-muted mb-0">Loading financial statements…</p>
      )}

      {report && (
        <>
          <div className="form-wrap balace-sheetwrap">
            <div className="row mb-3">
              <div className="col-md text-center">
                <h2 className="table-title mb-1">{report.pump.name}</h2>
                {report.pump.dealer ? (
                  <h3 className="table-title mb-1">{report.pump.dealer}</h3>
                ) : null}
                {report.pump.address ? (
                  <h3 className="table-title mb-1">{report.pump.address}</h3>
                ) : null}
                <h5 className="mt-2">
                  {report.period.start} to {report.period.end}
                </h5>
              </div>
            </div>

            <h4 className="table-title text-center mb-2">Profit &amp; Loss Account</h4>
            <div className="row sheet-wrp mb-4">
              <div className="col-md-12">
                <table className="table table-bordered border sheet-table">
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><th colSpan="2">Income</th></tr>
                    {pl.income.length === 0 ? (
                      <tr><td>No income recorded</td><td className="text-end">{moneyFormat(0)}</td></tr>
                    ) : (
                      pl.income.map((row) => (
                        <tr key={`inc-${row.head_id}`}>
                          <td className="ps-4">{row.label}</td>
                          <td className="text-end">{moneyFormat(row.amount)}</td>
                        </tr>
                      ))
                    )}
                    <tr>
                      <th>Total Income (A)</th>
                      <th className="text-end">{moneyFormat(pl.total_income)}</th>
                    </tr>
                    <tr><th colSpan="2">Expenses</th></tr>
                    {pl.expense.length === 0 ? (
                      <tr><td>No expenses recorded</td><td className="text-end">{moneyFormat(0)}</td></tr>
                    ) : (
                      pl.expense.map((row) => (
                        <tr key={`exp-${row.head_id}`}>
                          <td className="ps-4">{row.label}</td>
                          <td className="text-end">{moneyFormat(row.amount)}</td>
                        </tr>
                      ))
                    )}
                    <tr>
                      <th>Total Expenses (B)</th>
                      <th className="text-end">{moneyFormat(pl.total_expense)}</th>
                    </tr>
                    <tr className="table-active">
                      <th>{pl.is_profit ? "Net Profit (A - B)" : "Net Loss (B - A)"}</th>
                      <th className="text-end">
                        {moneyFormat(pl.is_profit ? pl.net_profit : pl.net_loss)}
                      </th>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <h4 className="table-title text-center mb-2">Balance Sheet</h4>
            <h6 className="text-center mb-3">As on {report.period.end}</h6>
            <div className="row sheet-wrp">
              <div className="col-md-12">
                <table className="table table-bordered border sheet-table">
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="table-active"><th colSpan="2">Capital &amp; Liabilities</th></tr>
                    {bs.liabilities.map((group) => (
                      <React.Fragment key={`liab-${group.group}`}>
                        <tr><th colSpan="2" className="ps-3">{group.group}</th></tr>
                        {group.items.map((item) => (
                          <tr key={`liab-i-${item.head_id}`}>
                            <td className="ps-4">{item.label}</td>
                            <td className="text-end">{moneyFormat(item.amount)}</td>
                          </tr>
                        ))}
                        <tr>
                          <th className="ps-3">Total {group.group}</th>
                          <th className="text-end">{moneyFormat(group.total)}</th>
                        </tr>
                        {group.group === "Capital Account" && (
                          <tr className="text-muted fst-italic">
                            <td className="ps-4">
                              (Memo) Net {bs.capital_net_profit >= 0 ? "Profit" : "Loss"} for the period per P&amp;L
                            </td>
                            <td className="text-end">{moneyFormat(bs.capital_net_profit)}</td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {bs.owners_fund >= 0 && (
                      <tr>
                        <th className="ps-3">Owners Fund (Reserves &amp; Surplus - balancing)</th>
                        <th className="text-end">{moneyFormat(bs.owners_fund)}</th>
                      </tr>
                    )}
                    <tr className="table-active">
                      <th>Total Capital &amp; Liabilities</th>
                      <th className="text-end">{moneyFormat(bs.grand_total)}</th>
                    </tr>
                    <tr className="table-active"><th colSpan="2">Assets</th></tr>
                    {bs.assets.map((group) => (
                      <React.Fragment key={`asset-${group.group}`}>
                        <tr><th colSpan="2" className="ps-3">{group.group}</th></tr>
                        {group.items.map((item) => (
                          <tr key={`asset-i-${item.head_id}`}>
                            <td className="ps-4">{item.label}</td>
                            <td className="text-end">{moneyFormat(item.amount)}</td>
                          </tr>
                        ))}
                        <tr>
                          <th className="ps-3">Total {group.group}</th>
                          <th className="text-end">{moneyFormat(group.total)}</th>
                        </tr>
                      </React.Fragment>
                    ))}
                    {bs.owners_fund < 0 && (
                      <tr>
                        <th className="ps-3">Accumulated Loss (balancing)</th>
                        <th className="text-end">{moneyFormat(Math.abs(bs.owners_fund))}</th>
                      </tr>
                    )}
                    <tr className="table-active">
                      <th>Total Assets</th>
                      <th className="text-end">{moneyFormat(bs.grand_total)}</th>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="text-end mt-4">
            <button
              type="button"
              className="btn btn-success px-3 mb-md-0 mb-3 me-3"
              onClick={() => downloadFile("pdf")}
              disabled={!!downloading}
            >
              {downloading === "pdf" ? "Preparing PDF..." : "Download PDF"}
            </button>
            <button
              type="button"
              className="btn btn-success px-3 mb-md-0 mb-3"
              onClick={() => downloadFile("excel")}
              disabled={!!downloading}
            >
              {downloading === "excel" ? "Preparing Excel..." : "Download Excel"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FinancialStatementsPanel;
